import { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { trainingSessionsApi, registerTemplatesApi, safetyProgramsApi } from '../lib/api';
import api from '../lib/api';
import { useAuth } from '../contexts/AuthContext';
import { toast } from 'sonner';
import {
  GraduationCap,
  Plus,
  Link2,
  Copy,
  Download,
  FileText,
  Power,
  Trash2,
  Users,
  Calendar,
  CheckCircle2,
  XCircle,
  ExternalLink,
  Upload,
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import { Textarea } from '../components/ui/textarea';

const todayISO = () => new Date().toISOString().split('T')[0];

const addDaysISO = (iso, n) => {
  const d = new Date(iso + 'T00:00:00');
  d.setDate(d.getDate() + n);
  return d.toISOString().split('T')[0];
};

// v1.3.22: giornate/orari pre-compilati per livello di rischio (modificabili a mano).
const buildGiornateForLevel = (lvl) => {
  const d0 = todayISO();
  const d1 = addDaysISO(d0, 1);
  const M = { orario_inizio: '09.00', orario_fine: '13.00' };
  const P = { orario_inizio: '14.00', orario_fine: '18.00' };
  if (lvl === 'alto') return [{ data: d0, ...M }, { data: d0, ...P }, { data: d1, ...M }];
  if (lvl === 'medio') return [{ data: d0, ...M }, { data: d0, ...P }];
  if (lvl === 'basso' || lvl === 'generale') return [{ data: d0, ...M }];
  return null;
};
const HOURS_BY_LEVEL = { alto: 12, medio: 8, basso: 4, generale: 4 };

// v1.3.22: antepone SEMPRE il titolo professionale (Ing./Arch./Dott./Avv./Sig...) al
// nome, evitando la doppia ripetizione se già presente.
const withTitolo = (titolo, nome) => {
  const t = (titolo || '').trim();
  const n = (nome || '').trim();
  if (!t) return n;
  if (!n) return n;
  if (n.toLowerCase().startsWith(t.toLowerCase())) return n;
  return `${t} ${n}`;
};

// Mappa livello → ore di default per il registro Antincendio
const LIVELLI_ANTINCENDIO = [
  { value: '', label: 'Nessuno (template non antincendio)', ore: null },
  { value: '1FOR', label: '1 FOR (Rischio Basso)', ore: 4 },
  { value: '2FOR', label: '2 FOR (Rischio Medio)', ore: 8 },
  { value: '3FOR', label: '3 FOR (Rischio Alto)', ore: 16 },
  { value: 'AGG_1FOR', label: 'Aggiornamento 1 FOR', ore: 2 },
  { value: 'AGG_2FOR', label: 'Aggiornamento 2 FOR', ore: 5 },
  { value: 'AGG_3FOR', label: 'Aggiornamento 3 FOR', ore: 8 },
];

const initialForm = {
  title: '',
  template_id: '',
  codice_progetto: '',
  codice_corso: '',
  livello: '',
  ragione_sociale: '',
  ente_organizzatore: '',
  giornate: [{ data: todayISO(), orario_inizio: '09.00', orario_fine: '13.00' }],
  sede: 'Videoconferenza',
  total_hours: 4,
  docente_nome: '',
  responsabile_nome: '',
  notes: '',
  also_opn: false,
  opn_template_ids: [],
  safety_program_id: '', // v38c: programma sicurezza associato
};

export default function TrainingSessionsPage() {
  const { user } = useAuth();
  const [sessions, setSessions] = useState([]);
  const [templates, setTemplates] = useState([]);
  const [safetyPrograms, setSafetyPrograms] = useState([]); // v38c
  const [loading, setLoading] = useState(true);
  const [showCreate, setShowCreate] = useState(false);
  const [creating, setCreating] = useState(false);
  const [form, setForm] = useState(initialForm);
  const [selected, setSelected] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [showAttendance, setShowAttendance] = useState(false);
  const [attendanceForm, setAttendanceForm] = useState([]);
  const [savingAttendance, setSavingAttendance] = useState(false);

  const load = async () => {
    try {
      setLoading(true);
      const [sessRes, tplRes, progRes] = await Promise.all([
        trainingSessionsApi.getAll(),
        registerTemplatesApi.getAll().catch(() => ({ data: [] })),
        safetyProgramsApi.getAll().catch(() => ({ data: [] })),
      ]);
      setSessions(sessRes.data);
      setTemplates((tplRes.data || []).filter((t) => t.is_active && t.file_exists));
      setSafetyPrograms((progRes.data || []).filter((p) => p.is_active !== false));
    } catch (e) {
      toast.error('Errore caricamento sessioni');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const templateName = (id) => {
    if (!id) {
      const def = templates.find((t) => t.is_default);
      return def ? `${def.name} (predefinito)` : 'Predefinito';
    }
    const t = templates.find((x) => x.id === id);
    return t ? t.name : 'Predefinito';
  };

  const publicUrlFor = (token) => `${window.location.origin}/public/registro/${token}`;

  const handleCreate = async (e) => {
    e.preventDefault();
    if (!form.title) {
      toast.error('Titolo obbligatorio');
      return;
    }
    if (!form.codice_progetto || !form.codice_progetto.trim()) {
      toast.error('Inserisci il Codice Progetto');
      return;
    }
    // Validazioni specifiche per registro Antincendio
    const tplSel = templates.find((t) => t.id === form.template_id);
    const isAntincendio = tplSel && /antincend/i.test(tplSel.name || '');
    if (isAntincendio) {
      if (!form.livello) {
        toast.error('Seleziona il livello Antincendio (1FOR/2FOR/3FOR o aggiornamento)');
        return;
      }
      if (!form.codice_corso || !form.codice_corso.trim()) {
        toast.error('Inserisci il Codice Corso univoco');
        return;
      }
    }
    const giornate = (form.giornate || []).filter((g) => g.data);
    if (giornate.length === 0) {
      toast.error('Aggiungi almeno una giornata di corso');
      return;
    }
    // Auto-calcola data inizio/fine + totale ore
    const sorted = [...giornate].sort((a, b) => a.data.localeCompare(b.data));
    const course_date_from = sorted[0].data;
    const course_date_to = sorted[sorted.length - 1].data;
    const calcHours = (g) => {
      const toMin = (t) => {
        if (!t) return null;
        const x = t.replace(',', '.').replace(':', '.');
        const [h, m] = x.split('.').map((v) => parseInt(v, 10));
        return isFinite(h) ? h * 60 + (isFinite(m) ? m : 0) : null;
      };
      const a = toMin(g.orario_inizio);
      const b = toMin(g.orario_fine);
      return a != null && b != null && b > a ? (b - a) / 60 : 0;
    };
    const total_hours = giornate.reduce((sum, g) => sum + calcHours(g), 0);

    try {
      setCreating(true);
      const payload = {
        ...form,
        course_date_from,
        course_date_to,
        total_hours: total_hours || form.total_hours,
        orario: giornate.length === 1
          ? `${giornate[0].orario_inizio}-${giornate[0].orario_fine}`
          : undefined,
      };
      const res = await trainingSessionsApi.create(payload);
      toast.success('Sessione creata');
      setShowCreate(false);
      setForm(initialForm);
      setSessions((s) => [res.data, ...s]);
      setSelected(res.data);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Errore creazione');
    } finally {
      setCreating(false);
    }
  };

  const openDetail = async (id) => {
    try {
      setDetailLoading(true);
      const res = await trainingSessionsApi.getOne(id);
      setSelected(res.data);
    } catch {
      toast.error('Errore caricamento dettaglio');
    } finally {
      setDetailLoading(false);
    }
  };

  const copyLink = (token) => {
    const url = publicUrlFor(token);
    navigator.clipboard.writeText(url);
    toast.success('Link copiato negli appunti');
  };

  const toggleActive = async (id) => {
    try {
      const res = await trainingSessionsApi.toggleActive(id);
      toast.success(res.data.is_active ? 'Sessione riattivata' : 'Sessione disattivata');
      setSessions((list) => list.map((s) => (s.id === id ? res.data : s)));
      if (selected?.id === id) setSelected(res.data);
    } catch {
      toast.error('Errore aggiornamento');
    }
  };

  const deleteSession = async (id) => {
    if (!window.confirm('Eliminare questa sessione? I dati degli iscritti andranno persi.')) return;
    try {
      await trainingSessionsApi.delete(id);
      toast.success('Sessione eliminata');
      setSessions((list) => list.filter((s) => s.id !== id));
      if (selected?.id === id) setSelected(null);
    } catch {
      toast.error('Errore eliminazione');
    }
  };

  const deleteEnrollment = async (sessionId, enrollmentId) => {
    if (!window.confirm('Rimuovere questo iscritto?')) return;
    try {
      const res = await trainingSessionsApi.deleteEnrollment(sessionId, enrollmentId);
      toast.success('Iscritto rimosso');
      setSelected(res.data);
      setSessions((list) => list.map((s) => (s.id === sessionId ? res.data : s)));
    } catch {
      toast.error('Errore rimozione');
    }
  };

  const downloadDocx = (id) => {
    window.open(trainingSessionsApi.getDocxUrl(id), '_blank');
  };
  const downloadDocxOpnForTemplate = (sessionId, opnTemplateId) => {
    // v38b: download di UNO specifico registro OPN selezionato dall'utente
    window.open(trainingSessionsApi.getDocxOpnUrlForTemplate(sessionId, opnTemplateId), '_blank');
  };
  const downloadPdf = (id) => {
    window.open(trainingSessionsApi.getPdfUrl(id), '_blank');
  };

  // === Attendance helpers ===
  const initAttendanceFromSession = (s) => {
    const enrollments = s?.enrollments || [];
    const giornate = s?.giornate && s.giornate.length ? s.giornate : [
      { data: s?.course_date_from || todayISO(), orario_inizio: '09.00', orario_fine: '13.00' },
    ];
    const existing = s?.attendance || [];
    return enrollments.map((_, idx) => {
      const prev = existing[idx] || {};
      const prevPresenze = prev.presenze || [];
      return {
        test_superato: prev.test_superato !== undefined ? prev.test_superato : true,
        partecipazione_ok: prev.partecipazione_ok !== undefined ? prev.partecipazione_ok : true,
        presenze: giornate.map((g, di) => {
          const pp = prevPresenze[di] || {};
          return {
            presente: pp.presente !== undefined ? pp.presente : true,
            orario_entrata: pp.orario_entrata || g.orario_inizio || '09.00',
            orario_uscita: pp.orario_uscita || g.orario_fine || '13.00',
          };
        }),
      };
    });
  };

  useEffect(() => {
    if (showAttendance && selected) {
      setAttendanceForm(initAttendanceFromSession(selected));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showAttendance, selected]);

  const calcTotal = (entrata, uscita) => {
    const toMin = (t) => {
      if (!t) return null;
      const x = String(t).replace(',', '.').replace(':', '.');
      const [h, m] = x.split('.').map((v) => parseInt(v, 10));
      return Number.isFinite(h) ? h * 60 + (Number.isFinite(m) ? m : 0) : null;
    };
    const a = toMin(entrata);
    const b = toMin(uscita);
    if (a == null || b == null || b <= a) return 0;
    return (b - a) / 60;
  };

  const saveAttendance = async () => {
    if (!selected) return;
    try {
      setSavingAttendance(true);
      const res = await trainingSessionsApi.updateAttendance(selected.id, { attendance: attendanceForm });
      toast.success('Presenze salvate');
      setSelected(res.data);
      setSessions((list) => list.map((s) => (s.id === selected.id ? res.data : s)));
      setShowAttendance(false);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Errore salvataggio');
    } finally {
      setSavingAttendance(false);
    }
  };

  return (
    <div className="space-y-6" data-testid="training-sessions-page">
      <motion.div
        initial={{ opacity: 0, y: -20 }}
        animate={{ opacity: 1, y: 0 }}
        className="flex items-center justify-between"
      >
        <div>
          <h1 className="text-3xl font-bold text-gray-900 flex items-center gap-2">
            <GraduationCap className="w-8 h-8 text-blue-600" />
            Registri Presenze Pubblici
          </h1>
          <p className="text-gray-600 mt-1">
            Genera un link pubblico: i discenti si iscrivono online e il registro viene compilato automaticamente.
          </p>
        </div>
        <Button
          onClick={() => {
            // v85: auto-popola docente e responsabile progetto formativo con utente corrente
            // v1.3.22: antepone SEMPRE il titolo professionale al nome (Ing./Arch./Dott...)
            const baseName = user?.full_name || user?.name || user?.email || '';
            const userName = withTitolo(user?.titolo_professionale, baseName);
            setForm({
              ...initialForm,
              docente_nome: userName,
              responsabile_nome: userName,
            });
            setShowCreate(true);
          }}
          className="bg-blue-600 hover:bg-blue-700"
          data-testid="new-session-btn"
        >
          <Plus className="w-4 h-4 mr-2" /> Nuova sessione
        </Button>
      </motion.div>

      {loading ? (
        <div className="text-center py-10 text-gray-500">Caricamento...</div>
      ) : sessions.length === 0 ? (
        <Card>
          <CardContent className="py-16 text-center text-gray-500">
            <GraduationCap className="w-16 h-16 mx-auto mb-4 opacity-30" />
            Nessuna sessione creata. Clicca "Nuova sessione" per iniziare.
          </CardContent>
        </Card>
      ) : (
        <div className="grid gap-4">
          {sessions.map((s) => (
            <Card
              key={s.id}
              className="hover:shadow-md transition cursor-pointer"
              onClick={() => openDetail(s.id)}
              data-testid={`session-card-${s.id}`}
            >
              <CardContent className="py-4">
                <div className="flex flex-wrap items-center justify-between gap-3">
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2 mb-1">
                      <h3 className="text-lg font-semibold text-gray-900 truncate">{s.title}</h3>
                      {s.is_active ? (
                        <Badge className="bg-emerald-100 text-emerald-800">
                          <CheckCircle2 className="w-3 h-3 mr-1" /> Attivo
                        </Badge>
                      ) : (
                        <Badge className="bg-gray-200 text-gray-700">
                          <XCircle className="w-3 h-3 mr-1" /> Disattivato
                        </Badge>
                      )}
                    </div>
                    <div className="flex flex-wrap gap-4 text-sm text-gray-600">
                      <span className="flex items-center gap-1">
                        <Calendar className="w-4 h-4" />
                        {s.course_date_from}
                        {s.course_date_to && s.course_date_to !== s.course_date_from
                          ? ` → ${s.course_date_to}`
                          : ''}
                      </span>
                      <span className="flex items-center gap-1">
                        <Users className="w-4 h-4" />
                        {s.enrollments?.length || 0} iscritti
                      </span>
                      {s.docente_nome && <span>Docente: {s.docente_nome}</span>}
                      {s.ragione_sociale && <span>Azienda: {s.ragione_sociale}</span>}
                      <span className="flex items-center gap-1 text-blue-700">
                        <FileText className="w-3 h-3" />
                        {templateName(s.template_id)}
                      </span>
                      {(s.opn_template_ids || []).length > 0 && (
                        <span className="flex items-center gap-1 px-1.5 py-0.5 rounded bg-purple-100 text-purple-800 text-[10px] font-semibold uppercase tracking-wide">
                          + {s.opn_template_ids.length} OPN
                        </span>
                      )}
                    </div>
                  </div>
                  <div className="flex items-center gap-2" onClick={(e) => e.stopPropagation()}>
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => copyLink(s.public_token)}
                      data-testid={`copy-link-${s.id}`}
                    >
                      <Link2 className="w-4 h-4 mr-1" /> Copia link
                    </Button>
                    <Button size="sm" variant="outline" onClick={() => openDetail(s.id)}>
                      <Users className="w-4 h-4 mr-1" /> Dettagli
                    </Button>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* Create dialog */}
      <Dialog open={showCreate} onOpenChange={setShowCreate}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Nuova sessione formativa</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleCreate} className="space-y-4">
            <div className="space-y-1">
              <Label>Titolo corso *</Label>
              <Input
                value={form.title}
                onChange={(e) => setForm({ ...form, title: e.target.value })}
                placeholder="es. Salute e Sicurezza sui luoghi di lavoro - formazione generale"
                data-testid="form-title"
                required
              />
            </div>
            <div className="space-y-1">
              <Label>Tipo di registro (template) *</Label>
              <Select
                value={form.template_id || '__default__'}
                onValueChange={(value) => {
                  const newTemplateId = value === '__default__' ? '' : value;
                  // v88: auto-selezione programma + OPN basata sul livello di rischio rilevato dal nome template
                  const tpl = templates.find((t) => t.id === newTemplateId);
                  const tplName = (tpl?.name || '').toLowerCase();
                  // Rileva livello rischio (basso/medio/alto) e categoria (generale/specifico)
                  const detectLevel = (s) => {
                    if (/\balto\b|high/i.test(s)) return 'alto';
                    if (/\bmedio\b|medium/i.test(s)) return 'medio';
                    if (/\bbasso\b|low/i.test(s)) return 'basso';
                    if (/\bgenerale\b|general/i.test(s)) return 'generale';
                    return null;
                  };
                  const level = detectLevel(tplName);
                  // v1.3.22: rileva anche la CATEGORIA per abbinare il programma giusto
                  // (specifica vs antincendio vs generale vs primo soccorso...).
                  const detectCategoria = (s) => {
                    if (/antincendio|incendi/i.test(s)) return 'antincendio';
                    if (/primo\s*soccorso/i.test(s)) return 'primo_soccorso';
                    if (/prepost/i.test(s)) return 'preposti';
                    if (/dirigent/i.test(s)) return 'dirigenti';
                    if (/\brls\b/i.test(s)) return 'rls';
                    if (/specific/i.test(s)) return 'specifica';
                    if (/general/i.test(s)) return 'generale';
                    return null;
                  };
                  const tplCat = detectCategoria(tplName);
                  const updates = { template_id: newTemplateId };
                  if (level) {
                    // Auto-match safety_program per LIVELLO + CATEGORIA (fallback: solo livello)
                    const progMatches = (p) => {
                      const pn = (p.name || p.title || '');
                      if (detectLevel(pn) !== level) return false;
                      if (!tplCat) return true;
                      const pc = (p.category || '').toLowerCase();
                      return pc === tplCat || detectCategoria(pn) === tplCat;
                    };
                    const matchedProgram =
                      safetyPrograms.find(progMatches) ||
                      safetyPrograms.find((p) => detectLevel(p.name || p.title || '') === level);
                    if (matchedProgram) {
                      updates.safety_program_id = matchedProgram.id;
                    }
                    // Auto-flag OPN templates con livello uguale (escludendo il template principale)
                    const matchedOpnIds = templates
                      .filter((t) => t.id !== newTemplateId && t.is_active)
                      .filter((t) => {
                        const n = (t.name || '').toLowerCase();
                        if (!/opn|specifico|generale|in[\s_-]?training/i.test(n)) return false;
                        return detectLevel(n) === level;
                      })
                      .map((t) => t.id);
                    if (matchedOpnIds.length > 0) {
                      updates.opn_template_ids = matchedOpnIds;
                      updates.also_opn = true;
                    }
                    // v1.3.22: pre-compila le giornate del corso in base al livello
                    // (Alto 12h, Medio 8h, Basso/Generale 4h) — modificabili a mano.
                    const giornatePreset = buildGiornateForLevel(level);
                    if (giornatePreset) {
                      updates.giornate = giornatePreset;
                      updates.total_hours = HOURS_BY_LEVEL[level] || form.total_hours;
                    }
                  }
                  setForm({ ...form, ...updates });
                }}
              >
                <SelectTrigger data-testid="form-template">
                  <SelectValue placeholder="Seleziona template" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="__default__">
                    Template predefinito{templates.find((t) => t.is_default)
                      ? ` (${templates.find((t) => t.is_default).name})`
                      : ''}
                  </SelectItem>
                  {templates
                    .filter((t) => !t.is_default)
                    .map((t) => (
                      <SelectItem key={t.id} value={t.id}>
                        {t.name}
                      </SelectItem>
                    ))}
                </SelectContent>
              </Select>
              {templates.length <= 1 && (
                <p className="text-xs text-gray-500">
                  Vai su <strong>Template Registri</strong> per caricare nuovi modelli .docx (es. formazione specifica, antincendio).
                </p>
              )}
            </div>
            {/* v38c: Selezione PROGRAMMA SICUREZZA */}
            <div className="border-l-4 border-blue-500 bg-blue-50 rounded p-3">
              <Label className="font-semibold text-sm text-blue-900">
                Programma Sicurezza
              </Label>
              <p className="text-xs text-blue-800 mb-2">
                Seleziona un programma per popolare automaticamente i placeholder
                <code className="bg-blue-100 px-1 mx-0.5 rounded">«Programma_*»</code>
                (titolo, ore, moduli, argomenti, normativa) nei registri OPN.
              </p>
              <Select
                value={form.safety_program_id || '__none__'}
                onValueChange={(v) => {
                  if (v === '__none__') {
                    setForm({ ...form, safety_program_id: '' });
                  } else {
                    const prog = safetyPrograms.find((p) => p.id === v);
                    // Auto-imposta ore totali e seleziona automaticamente i template OPN
                    // se non già selezionati (UX punto 4A confermato dall'utente).
                    setForm({
                      ...form,
                      safety_program_id: v,
                      total_hours: prog?.ore_totali || form.total_hours,
                      // Se ci sono template OPN nel sistema e nessuno selezionato,
                      // pre-seleziona tutti automaticamente (l'utente può deselezionare).
                      opn_template_ids: form.opn_template_ids && form.opn_template_ids.length > 0
                        ? form.opn_template_ids
                        : templates.filter((t) => /OPN/i.test(t.name || '')).map((t) => t.id),
                      also_opn: true,
                    });
                  }
                }}
              >
                <SelectTrigger data-testid="form-safety-program">
                  <SelectValue placeholder="— Nessun programma associato —" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="__none__">— Nessuno —</SelectItem>
                  {safetyPrograms.map((p) => (
                    <SelectItem key={p.id} value={p.id}>
                      {p.name} ({p.ore_totali}h)
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {form.safety_program_id && (() => {
                const prog = safetyPrograms.find((p) => p.id === form.safety_program_id);
                if (!prog) return null;
                return (
                  <div className="mt-2 text-xs text-blue-900 bg-white rounded p-2 border border-blue-200">
                    <div className="font-mono text-blue-700">{prog.code}</div>
                    <div className="italic">{prog.normativa}</div>
                    <div className="mt-1">
                      <strong>{(prog.moduli || []).length} moduli</strong> · {prog.destinatari}
                    </div>
                  </div>
                );
              })()}
            </div>

            {/* v38b: Selezione TEMPLATE OPN aggiuntivi da generare */}
            <div className="border-l-4 border-purple-500 bg-purple-50 rounded p-3">
              <div className="font-semibold text-sm text-purple-900 mb-1">
                Registri OPN aggiuntivi (opzionale)
              </div>
              <p className="text-xs text-purple-800 mb-2">
                Seleziona uno o più registri <strong>OPN</strong> (In Training SRLS) da generare in aggiunta
                al template principale, usando gli stessi dati della sessione. Utili per Generale,
                Specifico Rischio Basso/Medio/Alto.
              </p>
              <div className="space-y-1">
                {templates
                  .filter((t) => {
                    if (!t.is_active) return false;
                    const name = (t.name || '').toLowerCase();
                    // v87b FIX: match più tollerante per non perdere registri OPN/InTraining caricati con nomi diversi
                    return /opn|in[\s_-]?training|specifico\s*(basso|medio|alto)|formazione\s*generale/i.test(name)
                      || t.category === 'opn'
                      || t.category === 'register_opn';
                  })
                  .map((t) => {
                    const checked = (form.opn_template_ids || []).includes(t.id);
                    return (
                      <label
                        key={t.id}
                        className="flex items-center gap-2 text-sm cursor-pointer hover:bg-purple-100 rounded p-1.5"
                      >
                        <input
                          type="checkbox"
                          checked={checked}
                          onChange={(e) => {
                            const cur = form.opn_template_ids || [];
                            const next = e.target.checked
                              ? [...cur, t.id]
                              : cur.filter((x) => x !== t.id);
                            setForm({ ...form, opn_template_ids: next, also_opn: next.length > 0 });
                          }}
                          className="w-4 h-4"
                          data-testid={`form-opn-${t.id}`}
                        />
                        <span className="flex-1">{t.name}</span>
                      </label>
                    );
                  })}
                {templates.filter((t) => /OPN/i.test(t.name || '') && t.is_active).length === 0 && (
                  <p className="text-xs text-purple-700 italic">
                    Nessun template OPN trovato. Vai su <strong>Template Registri</strong> per
                    caricare i tuoi template OPN (Generale, Specifico Basso, Medio, Alto).
                  </p>
                )}
              </div>
            </div>
            <div className="space-y-1">
              <Label>Codice Progetto *</Label>
              <Input
                value={form.codice_progetto}
                onChange={(e) => setForm({ ...form, codice_progetto: e.target.value })}
                placeholder="es. PROG-2026-001"
                data-testid="form-codice-progetto"
                required
              />
              <p className="text-xs text-gray-500">
                Verrà inserito automaticamente nel registro (sostituisce il segnaposto "codice progetto" nel titolo del corso).
              </p>
            </div>
            {(() => {
              const tpl = templates.find((t) => t.id === form.template_id);
              const isAntincendio = tpl && /antincend/i.test(tpl.name || '');
              if (!isAntincendio) return null;
              return (
                <div className="grid grid-cols-2 gap-4 border rounded-md p-3 bg-orange-50/50">
                  <div className="space-y-1">
                    <Label>Livello Antincendio *</Label>
                    <Select
                      value={form.livello || ''}
                      onValueChange={(v) => {
                        const found = LIVELLI_ANTINCENDIO.find((l) => l.value === v);
                        setForm({
                          ...form,
                          livello: v,
                          total_hours: found && found.ore != null ? found.ore : form.total_hours,
                        });
                      }}
                    >
                      <SelectTrigger data-testid="form-livello">
                        <SelectValue placeholder="Seleziona livello..." />
                      </SelectTrigger>
                      <SelectContent>
                        {LIVELLI_ANTINCENDIO.filter((l) => l.value).map((l) => (
                          <SelectItem key={l.value} value={l.value}>
                            {l.label} — {l.ore} ore
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <p className="text-xs text-gray-500">
                      Le ore di formazione si aggiornano automaticamente in base al livello.
                    </p>
                  </div>
                  <div className="space-y-1">
                    <Label>Codice Corso (univoco) *</Label>
                    <Input
                      value={form.codice_corso}
                      onChange={(e) => setForm({ ...form, codice_corso: e.target.value })}
                      placeholder="es. ANT-2026-001"
                      data-testid="form-codice-corso"
                    />
                    <p className="text-xs text-gray-500">
                      Deve essere univoco. Non possono esistere due corsi con lo stesso codice.
                    </p>
                  </div>
                </div>
              );
            })()}
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-1">
                <Label>Ragione sociale azienda</Label>
                <Input
                  value={form.ragione_sociale}
                  onChange={(e) => setForm({ ...form, ragione_sociale: e.target.value })}
                  placeholder="es. ACME S.R.L."
                />
              </div>
              <div className="space-y-1">
                <Label>Ente organizzatore</Label>
                <Input
                  value={form.ente_organizzatore}
                  onChange={(e) => setForm({ ...form, ente_organizzatore: e.target.value })}
                  placeholder="Lascia vuoto per mantenere quello del template"
                />
              </div>
            </div>
            <div className="space-y-1">
              <Label>Giornate del corso *</Label>
              <p className="text-xs text-gray-500">
                Aggiungi una riga per ogni giornata. La data inizio/fine e le ore totali del corso vengono calcolate automaticamente.
              </p>
              <div className="border rounded-md p-3 space-y-2 bg-gray-50">
                {(form.giornate || []).map((g, idx) => (
                  <div key={idx} className="grid grid-cols-[1fr_120px_120px_40px] gap-2 items-end">
                    <div>
                      <Label className="text-xs">Data</Label>
                      <Input
                        type="date"
                        value={g.data}
                        onChange={(e) => {
                          const next = [...form.giornate];
                          next[idx] = { ...next[idx], data: e.target.value };
                          setForm({ ...form, giornate: next });
                        }}
                        data-testid={`form-day-date-${idx}`}
                      />
                    </div>
                    <div>
                      <Label className="text-xs">Inizio</Label>
                      <Input
                        value={g.orario_inizio}
                        onChange={(e) => {
                          const next = [...form.giornate];
                          next[idx] = { ...next[idx], orario_inizio: e.target.value };
                          setForm({ ...form, giornate: next });
                        }}
                        placeholder="09.00"
                      />
                    </div>
                    <div>
                      <Label className="text-xs">Fine</Label>
                      <Input
                        value={g.orario_fine}
                        onChange={(e) => {
                          const next = [...form.giornate];
                          next[idx] = { ...next[idx], orario_fine: e.target.value };
                          setForm({ ...form, giornate: next });
                        }}
                        placeholder="13.00"
                      />
                    </div>
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      className="text-red-600"
                      onClick={() => {
                        const next = form.giornate.filter((_, i) => i !== idx);
                        setForm({ ...form, giornate: next.length ? next : [{ data: todayISO(), orario_inizio: '09.00', orario_fine: '13.00' }] });
                      }}
                      disabled={form.giornate.length <= 1}
                    >
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </div>
                ))}
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={() => {
                    setForm({
                      ...form,
                      giornate: [
                        ...(form.giornate || []),
                        { data: todayISO(), orario_inizio: '09.00', orario_fine: '13.00' },
                      ],
                    });
                  }}
                  data-testid="form-add-day"
                >
                  <Plus className="w-4 h-4 mr-1" /> Aggiungi giornata
                </Button>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-1">
                <Label>Sede</Label>
                <Input
                  value={form.sede}
                  onChange={(e) => setForm({ ...form, sede: e.target.value })}
                />
              </div>
              <div className="space-y-1">
                <Label>Docente</Label>
                <Input
                  value={form.docente_nome}
                  onChange={(e) => setForm({ ...form, docente_nome: e.target.value })}
                />
              </div>
            </div>
            <div className="space-y-1">
              <Label>Responsabile del progetto formativo</Label>
              <Input
                value={form.responsabile_nome}
                onChange={(e) => setForm({ ...form, responsabile_nome: e.target.value })}
              />
            </div>
            <div className="space-y-1">
              <Label>Note</Label>
              <Textarea
                value={form.notes}
                onChange={(e) => setForm({ ...form, notes: e.target.value })}
                rows={2}
              />
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setShowCreate(false)}>
                Annulla
              </Button>
              <Button type="submit" disabled={creating} data-testid="submit-session-btn">
                {creating ? 'Creazione...' : 'Crea e genera link'}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Detail dialog - v84: Fullscreen + Draggable */}
      <DraggableDetailModal
        open={!!selected}
        onClose={() => setSelected(null)}
        title="Dettagli sessione"
      >
          {detailLoading ? (
            <div className="text-center py-8">Caricamento...</div>
          ) : selected ? (
            <div className="space-y-4">
              <div className="bg-blue-50 p-4 rounded border border-blue-100">
                <h3 className="font-semibold text-gray-900">{selected.title}</h3>
                <div className="grid grid-cols-2 gap-2 text-sm mt-2">
                  <div><strong>Data:</strong> {selected.course_date_from}
                    {selected.course_date_to && selected.course_date_to !== selected.course_date_from
                      ? ` → ${selected.course_date_to}` : ''}
                  </div>
                  <div><strong>Ore:</strong> {selected.total_hours || 'N/D'}</div>
                  <div><strong>Sede:</strong> {selected.sede || 'N/D'}</div>
                  <div><strong>Docente:</strong> {selected.docente_nome || 'N/D'}</div>
                  <div className="col-span-2"><strong>Azienda:</strong> {selected.ragione_sociale || 'N/D'}</div>
                  <div className="col-span-2"><strong>Codice Progetto:</strong> {selected.codice_progetto || 'N/D'}</div>
                  <div className="col-span-2 flex items-center gap-2">
                    <FileText className="w-4 h-4 text-blue-600" />
                    <strong>Template:</strong> {templateName(selected.template_id)}
                  </div>
                </div>
              </div>

              <div className="bg-gray-50 p-4 rounded border">
                <div className="flex items-center justify-between mb-2">
                  <Label className="text-sm">Link pubblico iscrizione</Label>
                  <Badge variant={selected.is_active ? 'default' : 'secondary'}>
                    {selected.is_active ? 'Attivo' : 'Disattivato'}
                  </Badge>
                </div>
                <div className="flex gap-2">
                  <Input readOnly value={publicUrlFor(selected.public_token)} className="font-mono text-xs" />
                  <Button size="sm" onClick={() => copyLink(selected.public_token)}>
                    <Copy className="w-4 h-4" />
                  </Button>
                  <Button size="sm" variant="outline" onClick={() => window.open(publicUrlFor(selected.public_token), '_blank')}>
                    <ExternalLink className="w-4 h-4" />
                  </Button>
                </div>
                <p className="text-xs text-gray-500 mt-2">
                  Condividi questo link ai discenti. Si iscriveranno direttamente e appariranno qui sotto.
                </p>
              </div>

              <div>
                <div className="flex items-center justify-between mb-2">
                  <h4 className="font-semibold">
                    Iscritti ({selected.enrollments?.length || 0})
                  </h4>
                  <div className="flex gap-2 flex-wrap">
                    <input
                      type="file"
                      accept=".csv,.xlsx,.xls"
                      id="bulk-import-file"
                      style={{ display: 'none' }}
                      onChange={async (e) => {
                        const f = e.target.files?.[0];
                        if (!f) return;
                        const fd = new FormData();
                        fd.append('file', f);
                        try {
                          const res = await api.post(`/training-sessions/${selected.id}/bulk-import-attendees`, fd, {
                            headers: { 'Content-Type': 'multipart/form-data' },
                          });
                          const { inseriti, duplicati_saltati, errori } = res.data;
                          let msg = `${inseriti} discenti importati`;
                          if (duplicati_saltati) msg += ` · ${duplicati_saltati} duplicati saltati`;
                          if (errori?.length) msg += ` · ${errori.length} errori`;
                          if (errori?.length) {
                            toast.warning(msg + '\n' + errori.slice(0, 3).map(er => `Riga ${er.riga}: ${er.errore}`).join('\n'));
                          } else {
                            toast.success(msg);
                          }
                          // Refresh sessione
                          const updated = await trainingSessionsApi.get(selected.id);
                          setSelected(updated.data);
                          e.target.value = '';
                        } catch (err) {
                          toast.error(err.response?.data?.detail || 'Errore import');
                        }
                      }}
                      data-testid="bulk-import-input"
                    />
                    <Button
                      size="sm"
                      variant="outline"
                      className="border-emerald-500 text-emerald-700 hover:bg-emerald-50"
                      onClick={() => document.getElementById('bulk-import-file')?.click()}
                      title="Carica un file CSV o Excel con colonne: cognome, nome, codice_fiscale, luogo_nascita, data_nascita, azienda, telefono"
                      data-testid="bulk-import-btn"
                    >
                      <Upload className="w-4 h-4 mr-1" /> Import Massivo
                    </Button>
                    <Button size="sm" variant="outline" onClick={() => setShowAttendance(true)} disabled={!selected.enrollments?.length} data-testid="open-attendance-btn">
                      <CheckCircle2 className="w-4 h-4 mr-1" /> Compila Presenze
                    </Button>
                    <Button size="sm" onClick={() => downloadDocx(selected.id)} data-testid="download-docx-btn">
                      <Download className="w-4 h-4 mr-1" /> Scarica DOCX
                    </Button>
                    {(selected.opn_template_ids || []).map((opnId) => {
                      const t = templates.find((x) => x.id === opnId);
                      if (!t) return null;
                      const shortName = (t.name || 'OPN').replace(/^Registro\s+Presenze\s+/i, '');
                      return (
                        <Button
                          key={opnId}
                          size="sm"
                          variant="outline"
                          className="border-purple-500 text-purple-700 hover:bg-purple-50"
                          onClick={() => downloadDocxOpnForTemplate(selected.id, opnId)}
                          data-testid={`download-docx-opn-${opnId}`}
                          title={`Scarica ${t.name}`}
                        >
                          <Download className="w-4 h-4 mr-1" /> {shortName}
                        </Button>
                      );
                    })}
                    <Button size="sm" variant="outline" onClick={() => downloadPdf(selected.id)} data-testid="download-pdf-btn">
                      <FileText className="w-4 h-4 mr-1" /> Scarica PDF
                    </Button>
                  </div>
                </div>
                {!selected.enrollments || selected.enrollments.length === 0 ? (
                  <p className="text-center py-6 text-gray-500 border rounded">
                    Nessun iscritto ancora. Condividi il link pubblico.
                  </p>
                ) : (
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>#</TableHead>
                        <TableHead>Cognome</TableHead>
                        <TableHead>Nome</TableHead>
                        <TableHead>Codice Fiscale</TableHead>
                        <TableHead>Luogo nascita</TableHead>
                        <TableHead>Data nascita</TableHead>
                        <TableHead>Azienda</TableHead>
                        <TableHead></TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {selected.enrollments.map((e, i) => (
                        <TableRow key={e.id}>
                          <TableCell>{i + 1}</TableCell>
                          <TableCell className="font-medium">{e.cognome}</TableCell>
                          <TableCell>{e.nome}</TableCell>
                          <TableCell className="font-mono text-xs">{e.codice_fiscale}</TableCell>
                          <TableCell>{e.luogo_nascita}</TableCell>
                          <TableCell>{e.data_nascita}</TableCell>
                          <TableCell>{e.azienda || '-'}</TableCell>
                          <TableCell>
                            <Button
                              size="icon"
                              variant="ghost"
                              onClick={() => deleteEnrollment(selected.id, e.id)}
                              data-testid={`remove-enrollment-${e.id}`}
                            >
                              <Trash2 className="w-4 h-4 text-red-500" />
                            </Button>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                )}
              </div>
            </div>
          ) : null}
          <div className="gap-2 flex flex-wrap sm:flex-nowrap mt-4 pt-4 border-t">
            {selected && (
              <>
                <Button
                  variant="outline"
                  onClick={() => toggleActive(selected.id)}
                  data-testid="toggle-active-btn"
                >
                  <Power className="w-4 h-4 mr-1" />
                  {selected.is_active ? 'Disattiva link' : 'Riattiva link'}
                </Button>
                <Button
                  variant="destructive"
                  onClick={() => deleteSession(selected.id)}
                  data-testid="delete-session-btn"
                >
                  <Trash2 className="w-4 h-4 mr-1" /> Elimina sessione
                </Button>
                <Button onClick={() => setSelected(null)}>Chiudi</Button>
              </>
            )}
          </div>
        </DraggableDetailModal>

      {/* Compila Presenze Dialog */}
      <Dialog open={showAttendance} onOpenChange={setShowAttendance}>
        <DialogContent className="max-w-6xl max-h-[90vh] overflow-y-auto" data-testid="attendance-dialog">
          <DialogHeader>
            <DialogTitle>Compila Presenze ed Esiti</DialogTitle>
          </DialogHeader>
          {selected && (
            <div className="space-y-4">
              <p className="text-sm text-gray-600">
                Default: tutti presenti, test superato, partecipazione ≥ 90%. Modifica solo le righe che servono (es. uscita anticipata). Il totale ore si calcola in automatico.
              </p>
              {(selected.giornate && selected.giornate.length ? selected.giornate : [{ data: selected.course_date_from, orario_inizio: '09.00', orario_fine: '13.00' }]).map((g, gi) => (
                <div key={gi} className="border rounded-md p-3 space-y-2">
                  <div className="font-semibold text-sm">
                    Giornata {gi + 1}: {g.data} ({g.orario_inizio}-{g.orario_fine})
                  </div>
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead className="w-10">#</TableHead>
                        <TableHead>Discente</TableHead>
                        <TableHead className="w-24">Presente</TableHead>
                        <TableHead className="w-28">Entrata</TableHead>
                        <TableHead className="w-28">Uscita</TableHead>
                        <TableHead className="w-20">Tot. ore</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {(selected.enrollments || []).map((enr, ei) => {
                        const a = attendanceForm[ei];
                        const dayAtt = a?.presenze?.[gi] || {};
                        const tot = dayAtt.presente ? calcTotal(dayAtt.orario_entrata, dayAtt.orario_uscita) : 0;
                        const updateDay = (patch) => {
                          setAttendanceForm((prev) => prev.map((row, r) => {
                            if (r !== ei) return row;
                            const presenze = [...(row.presenze || [])];
                            presenze[gi] = { ...presenze[gi], ...patch };
                            return { ...row, presenze };
                          }));
                        };
                        return (
                          <TableRow key={enr.id || ei}>
                            <TableCell>{ei + 1}</TableCell>
                            <TableCell>{enr.cognome} {enr.nome}</TableCell>
                            <TableCell>
                              <input
                                type="checkbox"
                                checked={!!dayAtt.presente}
                                onChange={(e) => updateDay({ presente: e.target.checked })}
                                data-testid={`att-pres-${ei}-${gi}`}
                              />
                            </TableCell>
                            <TableCell>
                              <Input
                                value={dayAtt.orario_entrata || ''}
                                onChange={(e) => updateDay({ orario_entrata: e.target.value })}
                                placeholder="09.00"
                                disabled={!dayAtt.presente}
                                className="h-8"
                              />
                            </TableCell>
                            <TableCell>
                              <Input
                                value={dayAtt.orario_uscita || ''}
                                onChange={(e) => updateDay({ orario_uscita: e.target.value })}
                                placeholder="13.00"
                                disabled={!dayAtt.presente}
                                className="h-8"
                              />
                            </TableCell>
                            <TableCell className="text-center font-semibold">
                              {Number.isInteger(tot) ? tot : tot.toFixed(1)}
                            </TableCell>
                          </TableRow>
                        );
                      })}
                    </TableBody>
                  </Table>
                </div>
              ))}

              <div className="border rounded-md p-3 space-y-2">
                <div className="font-semibold text-sm">Esiti finali</div>
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead className="w-10">#</TableHead>
                      <TableHead>Discente</TableHead>
                      <TableHead className="w-40">Partecipazione ≥ 90%</TableHead>
                      <TableHead className="w-40">Test superato</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {(selected.enrollments || []).map((enr, ei) => {
                      const a = attendanceForm[ei];
                      return (
                        <TableRow key={enr.id || ei}>
                          <TableCell>{ei + 1}</TableCell>
                          <TableCell>{enr.cognome} {enr.nome}</TableCell>
                          <TableCell>
                            <input
                              type="checkbox"
                              checked={!!a?.partecipazione_ok}
                              onChange={(e) => setAttendanceForm((prev) => prev.map((row, r) => r === ei ? { ...row, partecipazione_ok: e.target.checked } : row))}
                              data-testid={`att-part-${ei}`}
                            />
                          </TableCell>
                          <TableCell>
                            <input
                              type="checkbox"
                              checked={!!a?.test_superato}
                              onChange={(e) => setAttendanceForm((prev) => prev.map((row, r) => r === ei ? { ...row, test_superato: e.target.checked } : row))}
                              data-testid={`att-test-${ei}`}
                            />
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="ghost" onClick={() => setShowAttendance(false)}>Annulla</Button>
            <Button onClick={saveAttendance} disabled={savingAttendance} data-testid="save-attendance-btn">
              {savingAttendance ? 'Salvataggio...' : 'Salva presenze'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

// v84: Modale draggable + ridimensionabile per Dettagli Sessione
function DraggableDetailModal({ open, onClose, title, children }) {
  const [maximized, setMaximized] = useState(false);
  // v87b FIX: reset stato draggato ad ogni apertura per garantire centratura iniziale
  const [dragKey, setDragKey] = useState(0);
  useEffect(() => { if (open) { setDragKey(k => k + 1); setMaximized(false); } }, [open]);
  if (!open) return null;
  return (
    <div className="fixed inset-0 z-50">
      {/* Backdrop */}
      <div className="absolute inset-0 bg-black/40 backdrop-blur-sm" onClick={onClose} />
      {/* Wrapper FLEX per centratura — il modal vive dentro questo container */}
      <div className={`absolute inset-0 flex items-center justify-center pointer-events-none ${maximized ? 'p-2' : 'p-4'}`}>
        <motion.div
          key={dragKey}
          drag={!maximized}
          dragMomentum={false}
          dragElastic={0}
          initial={{ scale: 0.95, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          exit={{ scale: 0.95, opacity: 0 }}
          className={`pointer-events-auto bg-white shadow-2xl rounded-lg overflow-hidden flex flex-col ${
            maximized ? 'w-full h-full' : 'w-[min(1400px,95vw)] h-[min(900px,90vh)]'
          }`}
        >
        {/* Header (handle drag) */}
        <div className={`flex items-center justify-between bg-gradient-to-r from-blue-600 to-indigo-600 text-white px-4 py-2 ${maximized ? '' : 'cursor-move'} select-none`}>
          <div className="flex items-center gap-2 flex-1">
            <svg className="w-4 h-4 opacity-70" viewBox="0 0 24 24" fill="currentColor"><path d="M8 6h2v2H8zm0 5h2v2H8zm0 5h2v2H8zm6-10h2v2h-2zm0 5h2v2h-2zm0 5h2v2h-2z"/></svg>
            <h2 className="font-semibold text-base">{title}</h2>
          </div>
          <div className="flex items-center gap-1">
            <button onClick={() => setMaximized(m => !m)}
              className="w-7 h-7 rounded hover:bg-white/20 flex items-center justify-center text-sm"
              title={maximized ? 'Riduci' : 'Massimizza'} data-testid="modal-maximize">
              {maximized ? '🗗' : '🗖'}
            </button>
            <button onClick={onClose}
              className="w-7 h-7 rounded hover:bg-white/20 flex items-center justify-center text-lg"
              title="Chiudi" data-testid="modal-close">×</button>
          </div>
        </div>
        {/* Body */}
        <div className="flex-1 overflow-y-auto p-6">
          {children}
        </div>
      </motion.div>
      </div>
    </div>
  );
}

